import { motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { AdvisorContextRail } from "../advisor/advisor-context-rail";
import { AdvisorLiveChat } from "../advisor/advisor-live-chat";
import { PlatformTourController } from "../overview/platform-tour-modal";
import type { TourState } from "../overview/platform-tour-modal";
import {
  advisorContextCopyFor,
  resolveActiveAdvisorContext,
} from "../../lib/advisor-context";
import { useAdvisorDrawerStore } from "../../stores/advisor";
import type {
  AdvisorPanel,
  DashboardSnapshot,
  FinanceReadinessPreviewItem,
} from "../../lib/dashboard";
import { deriveHighestVerificationTier } from "../../lib/dashboard";
import type {
  ReadinessResult,
  ReadinessTarget,
} from "../../lib/readiness-types";
import {
  buildJourneyStepPredicates,
  type DashboardJourneyStep,
  getDashboardJourneySteps,
} from "../../lib/dashboard-milestones";
import { shouldShowStrip, useVerifiedStore } from "../../stores/verified";
import { VerificationStatusStrip } from "../verification/verification-status-strip";
import { Button } from "../ui/button";
import { PageHeader } from "../ui/page-header";
import {
  DataSourceChooser,
  type DataSourceOption,
} from "./overview/data-source-chooser";
import { FinanceReadinessPreview } from "./overview/finance-readiness-preview";
import { OverviewScorePanel } from "./overview/score-panel";
import { PropertiesPreview } from "./overview/properties-preview";
import { RateWatchPreview } from "./overview/rate-watch-preview";
import { JourneyRail } from "./journey-rail";
import { AddPropertyWidget } from "./journey-widgets/add-property-widget";
import { ProfileCompletenessWidget } from "./journey-widgets/profile-completeness-widget";
import { PromptSuggestionsWidget } from "./journey-widgets/prompt-suggestions-widget";
import { PublishPreviewWidget } from "./journey-widgets/publish-preview-widget";
export { ProfileView } from "./profile/profile-view";

const OVERVIEW_SECTION_TRANSITION = {
  duration: 0.24,
  ease: [0.2, 0, 0, 1] as const,
};

const JOURNEY_RESTORE_TOAST_SESSION_KEY =
  "sill.journey_rail_restore_toast.dismissed";

type OverviewGreetingOption = {
  solo: string;
  withName: (firstName: string) => string;
};

const OVERVIEW_GREETING_OPTIONS: readonly OverviewGreetingOption[] = [
  {
    solo: "Hello.",
    withName: (firstName) => `Hello, ${firstName}.`,
  },
  {
    solo: "Welcome back.",
    withName: (firstName) => `Welcome back, ${firstName}.`,
  },
  {
    solo: "Good to see you.",
    withName: (firstName) => `Good to see you, ${firstName}.`,
  },
  {
    solo: "Ready when you are.",
    withName: (firstName) => `Ready when you are, ${firstName}.`,
  },
  {
    solo: "Let's keep going.",
    withName: (firstName) => `Let's keep going, ${firstName}.`,
  },
];

function getRandomOverviewGreetingOption() {
  const index = Math.floor(Math.random() * OVERVIEW_GREETING_OPTIONS.length);
  return OVERVIEW_GREETING_OPTIONS[index] ?? OVERVIEW_GREETING_OPTIONS[0];
}

function formatOverviewGreeting(
  option: OverviewGreetingOption,
  firstName: string,
) {
  return firstName === "You" ? option.solo : option.withName(firstName);
}

function getJourneyRestoreToastDismissed() {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(
      window.sessionStorage.getItem(JOURNEY_RESTORE_TOAST_SESSION_KEY),
    );
  } catch {
    return false;
  }
}

function markJourneyRestoreToastDismissed() {
  try {
    window.sessionStorage.setItem(JOURNEY_RESTORE_TOAST_SESSION_KEY, "1");
  } catch {
    /* noop - purely cosmetic state */
  }
}

export interface OverviewViewProps {
  /**
   * Bootstrap platform tour state from /api/users/me app_state (M3).
   * null/undefined → missing backend state (auto-open allowed).
   * Passed through to PlatformTourController.
   */
  bootstrapTourState?: TourState | null;
  onAddProperty: () => void;
  // SILL-173: opens the advisor with a prompt prefilled. Routed here
  // because the prompt-suggestions widget needs to pass the chip's
  // label into advisor — the existing onJourneyStepAction can't carry
  // that payload cleanly.
  onAskSillPrompt?: (prompt: string) => void;
  onJourneyStepAction: (step: DashboardJourneyStep, actionKey: string) => void;
  onCompleteOnboarding: () => void;
  // SILL-173: rail dismissibility — when present, JourneyRail renders
  // the X button. Persistence (localStorage) is the controller's job.
  onDismissJourneyRail?: () => void;
  // SILL-173: restores the rail from the overview-only toast when the
  // persistent dismiss flag is true.
  onRestoreJourneyRail?: () => void;
  // SILL-173: when true, OverviewView omits the rail entirely (the
  // controller has read the persisted dismiss flag).
  journeyRailDismissed?: boolean;
  /**
   * Plan 435 M2 (mfh-home-route-gate-matrix review fix F2): journey steps
   * whose `id` is in this list are dropped from the rail before render.
   * `HomeView` passes `["publish-profile"]` while the consumer marketplace
   * gate is `off` — the `PublishPreviewWidget` step is marketplace-flavoured
   * (a publish-to-agents promise) and must not render pre-launch alongside
   * the readiness-variant hero.
   */
  hideJourneyStepIds?: readonly string[];
  onOpenFinancials: () => void;
  onOpenProperties: () => void;
  onOpenProperty: (id: string) => void;
  onOpenRateWatch: () => void;
  onScoreFocus: () => void;
  onSelectVerificationSource: (
    source: DataSourceOption,
    item?: FinanceReadinessPreviewItem,
  ) => void;
  onVerificationUpgrade: () => void;
  readinessLoading?: boolean;
  readiness?: ReadinessResult | null;
  readinessTarget?: ReadinessTarget | null;
  snapshot: DashboardSnapshot;
}

export function OverviewView({
  bootstrapTourState,
  onAddProperty,
  onAskSillPrompt,
  onJourneyStepAction,
  onCompleteOnboarding,
  onDismissJourneyRail,
  onRestoreJourneyRail,
  journeyRailDismissed = false,
  hideJourneyStepIds,
  onOpenFinancials,
  onOpenProperties,
  onOpenProperty,
  onOpenRateWatch,
  onScoreFocus,
  onSelectVerificationSource,
  onVerificationUpgrade,
  readinessLoading = false,
  readiness,
  readinessTarget,
  snapshot,
}: OverviewViewProps) {
  // Derived from the snapshot, never the verified store — the store's tier
  // is preview-only control state with no production writer (the sidebar
  // pill uses this same derivation, so the surfaces can't contradict).
  const currentTier = deriveHighestVerificationTier(snapshot);
  const verifiedAt = useVerifiedStore((s) => s.verifiedAt);
  const openExplainer = useVerifiedStore((s) => s.openExplainer);

  // SILL-173: each journey step lights up from its own observable
  // signal on the snapshot (predicate map), replacing the legacy
  // 5-milestone counter that was mismapped onto the 6-step rail.
  const journeySteps = useMemo(() => {
    const predicates = buildJourneyStepPredicates(snapshot);
    const steps = getDashboardJourneySteps({ predicates });
    if (!hideJourneyStepIds || hideJourneyStepIds.length === 0) return steps;
    return steps.filter((step) => !hideJourneyStepIds.includes(step.id));
  }, [snapshot, hideJourneyStepIds]);
  const journeyRailHidden = Boolean(journeyRailDismissed);
  const [restoreToastDismissed, setRestoreToastDismissed] = useState(
    getJourneyRestoreToastDismissed,
  );

  const showJourneyRestoreToast =
    journeyRailHidden && Boolean(onRestoreJourneyRail) && !restoreToastDismissed;

  const handleJourneyStepAction = (
    step: DashboardJourneyStep,
    actionKey: string,
  ) => {
    if (actionKey === "explain-tiers") {
      openExplainer();
      return;
    }
    onJourneyStepAction(step, actionKey);
  };

  const handleDismissRestoreToast = () => {
    markJourneyRestoreToastDismissed();
    setRestoreToastDismissed(true);
  };

  return (
    <>
      <OverviewGreeting firstName={snapshot.firstName} />
      <div className="mb-4">
        <PlatformTourController
          ready
          bootstrapTourState={bootstrapTourState}
        />
      </div>

      <div className="space-y-4">
        <motion.div
          data-overview-region="score-panel"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={OVERVIEW_SECTION_TRANSITION}
          layout
        >
          <OverviewScorePanel
            isReadinessLoading={readinessLoading}
            readiness={readiness}
            target={readinessTarget}
            snapshot={snapshot}
            onScoreFocus={onScoreFocus}
            onChangeTarget={onOpenProperties}
            onCompleteOnboarding={onCompleteOnboarding}
            onVerificationAction={onVerificationUpgrade}
          />
        </motion.div>

        {shouldShowStrip(currentTier) ? (
          <motion.div
            data-overview-region="verification-strip"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...OVERVIEW_SECTION_TRANSITION, delay: 0.03 }}
            layout
          >
            <VerificationStatusStrip
              currentTier={currentTier}
              verifiedAt={verifiedAt ?? undefined}
              segment={readiness?.segment}
              onUpgrade={onVerificationUpgrade}
              onExplainTiers={openExplainer}
            />
          </motion.div>
        ) : null}

        {journeyRailHidden ? null : (
          <motion.div
            data-overview-region="journey-rail"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...OVERVIEW_SECTION_TRANSITION, delay: 0.06 }}
            layout
          >
            <JourneyRail
              currentTier={currentTier}
              dismissible={Boolean(onDismissJourneyRail)}
              onDismiss={onDismissJourneyRail}
              onStepAction={handleJourneyStepAction}
              steps={journeySteps}
              widgetSlot={(step) => {
                switch (step.widgetKey) {
                  case "data-source-chooser":
                    return (
                      <DataSourceChooser
                        onSelect={onSelectVerificationSource}
                      />
                    );
                  case "profile-completeness":
                    return (
                      <ProfileCompletenessWidget
                        missing={snapshot.profileCompletenessMissing}
                        onContinue={onCompleteOnboarding}
                        percent={snapshot.profileCompleteness}
                      />
                    );
                  case "prompt-suggestions":
                    return (
                      <PromptSuggestionsWidget
                        onPrompt={(prompt) => onAskSillPrompt?.(prompt)}
                      />
                    );
                  case "add-property":
                    return (
                      <AddPropertyWidget
                        onAddProperty={onAddProperty}
                        trackedProperties={snapshot.trackedProperties}
                      />
                    );
                  case "publish-preview":
                    return (
                      <PublishPreviewWidget
                        onOpenPublish={() =>
                          handleJourneyStepAction(step, "publish-profile")
                        }
                        publishedAt={snapshot.publishedAt}
                      />
                    );
                  default:
                    return null;
                }
              }}
            />
          </motion.div>
        )}

        <motion.div
          className="grid items-stretch gap-3 xl:grid-cols-2"
          data-overview-region="two-up-grid"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...OVERVIEW_SECTION_TRANSITION, delay: 0.09 }}
          layout
        >
          <FinanceReadinessPreview
            items={snapshot.financeReadinessPreview ?? []}
            onAddFinancialDetails={onOpenFinancials}
            onOpenFinancials={onOpenFinancials}
            onRefreshItem={() => onOpenFinancials()}
            onSelectVerificationSource={onSelectVerificationSource}
            onVerifyItem={() => undefined}
          />
          <RateWatchPreview
            onOpenRateWatch={onOpenRateWatch}
            snapshot={snapshot}
          />
        </motion.div>

        <motion.div
          data-overview-region="properties-preview"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...OVERVIEW_SECTION_TRANSITION, delay: 0.12 }}
          layout
        >
          <PropertiesPreview
            trackedProperties={snapshot.trackedProperties}
            onOpenProperties={onOpenProperties}
            onOpenProperty={onOpenProperty}
            onAddProperty={onAddProperty}
          />
        </motion.div>
      </div>

      {showJourneyRestoreToast ? (
        <JourneyRestoreToast
          onDismiss={handleDismissRestoreToast}
          onRestore={() => onRestoreJourneyRail?.()}
        />
      ) : null}
    </>
  );
}

function JourneyRestoreToast({
  onDismiss,
  onRestore,
}: {
  onDismiss: () => void;
  onRestore: () => void;
}) {
  return (
    <motion.aside
      aria-label="Journey rail is hidden"
      aria-live="polite"
      animate={{ opacity: 1, y: 0 }}
      className="fixed bottom-[calc(env(safe-area-inset-bottom)+5.75rem)] left-4 right-4 z-40 rounded-2xl border border-primary/20 bg-primary-soft/95 p-4 shadow-lg backdrop-blur-sm sm:left-auto sm:w-[22rem] lg:bottom-6"
      data-slot="journey-rail-restore-toast"
      initial={{ opacity: 0, y: 10 }}
      role="region"
      transition={{ duration: 0.22, ease: [0.2, 0, 0, 1] }}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-semibold text-primary-dark">
            Journey hidden
          </p>
          <p className="text-sm leading-5 text-primary-dark/75">
            Bring back the step-by-step journey when you want it on Overview.
          </p>
        </div>
        <button
          aria-label="Dismiss journey reminder"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-primary-dark/70 transition-colors hover:bg-primary/10 hover:text-primary-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          data-slot="journey-rail-restore-toast-dismiss"
          onClick={onDismiss}
          type="button"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>
      <Button
        className="mt-3 min-h-10 rounded-xl px-3"
        data-testid="journey-rail-restore-toast-action"
        onClick={onRestore}
        size="sm"
        type="button"
      >
        Show Journey
        <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
      </Button>
    </motion.aside>
  );
}

/**
 * Plan 435 M3 (mfh-prepare-row): exported so `components/home/HomeView`
 * reuses the EXACT greeting (random rotation + description) instead of
 * forking a second, simpler greeting — `HomeView` no longer renders the
 * rest of `OverviewView`, but this one piece carries over verbatim.
 */
export function OverviewGreeting({ firstName }: { firstName: string }) {
  const [greetingOption] = useState(getRandomOverviewGreetingOption);
  const greeting = formatOverviewGreeting(greetingOption, firstName);

  return (
    // Greeting only (2026-10-08): the old "command center for readiness"
    // description no longer described the marketplace Home and cost three
    // lines above the hero, which already carries the page's state.
    <PageHeader className="px-1 pb-1 pt-2" title={greeting} />
  );
}

/**
 * FeedView RETIRED (Plan 287 M3): the static placeholder feed built from
 * `snapshot.feed` / `buildFeed()` has been replaced by the notification-backed
 * `/feed` route (`FeedPage`). This stub is exported for any remaining
 * import sites that have not yet been cleaned up; it renders nothing.
 *
 * @deprecated Use the /feed route + FeedPage instead.
 */
export function FeedView() {
  return null;
}

export function AdvisorView({
  initialPromptAction = "draft",
  initialPrompt,
  onProfileSnapshotUpdated,
  snapshot,
}: {
  activePanel?: AdvisorPanel;
  initialPromptAction?: "draft" | "submit_new";
  initialPrompt?: string;
  onPanelChange?: (panel: AdvisorPanel) => void;
  onProfileSnapshotUpdated?: () => void;
  onOpenSettings?: () => void;
  snapshot: DashboardSnapshot;
}) {
  // Plan 435 M6 (asd-store-context): context precedence is store first
  // (the drawer's Expand hand-off), the legacy `?context=` enum second
  // (external deep links) — `resolveActiveAdvisorContext`'s own doc
  // comment. A page refresh loses the in-memory store value and falls back
  // to the URL enum (or null) with no error, by construction.
  const [searchParams] = useSearchParams();
  const storeContext = useAdvisorDrawerStore((s) => s.activeContext);
  const activeContext = resolveActiveAdvisorContext(storeContext, searchParams);
  const contextCopy = advisorContextCopyFor(activeContext, snapshot);
  // Review fix (iteration 2, finding 1): `collapseDrawer`'s Expand hand-off
  // deliberately leaves `activeContext` set so THIS mount can read it
  // (§4.5, store-first) — but nothing cleared it afterward, so it leaked
  // into the next time the full page rendered with no drawer in between
  // (e.g. a SILL-173 prompt chip's `?context=` deep link reused the stale
  // entity context instead of its own enum). Clearing on unmount — i.e. the
  // moment the buyer leaves `?view=advisor` — consumes the hand-off exactly
  // once without touching `resolveActiveAdvisorContext`'s store-first
  // precedence while this mount is alive.
  const setActiveContext = useAdvisorDrawerStore((s) => s.setActiveContext);
  useEffect(() => {
    return () => {
      setActiveContext(null);
    };
  }, [setActiveContext]);
  return (
    <AdvisorLiveChat
      contextRail={
        <AdvisorContextRail contextCopy={contextCopy} snapshot={snapshot} />
      }
      // Plumb through the user's current profile snapshot so widgets
      // hydrated from past sessions can self-badge when they were
      // computed against superseded inputs (Stage 4).
      currentSnapshotId={snapshot.currentSnapshotId ?? null}
      initialPromptAction={initialPromptAction}
      initialPrompt={initialPrompt}
      onProfileSnapshotUpdated={onProfileSnapshotUpdated}
      snapshot={snapshot}
    />
  );
}
